import { useEffect, useMemo, useRef, useState } from "react";
import { ProjectBar } from "@/components/ProjectBar";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useExportGate } from "@/hooks/useExportGate";
import { ExportUsageNote } from "@/components/ExportUsageNote";
import { useProject } from "@/lib/workflow/useProject";
import { newId } from "@/lib/workflow/ids";
import { parseNumber } from "@/lib/workflow/fields";
import { describeImport, templateCsv } from "@/lib/workflow/layouts";
import { BUDGET_SPLITS, budgetReport, expenseGross, knownCategories, migrateLegacyBudget, splitBudget } from "@/lib/workflow/budgetOps";
import { exportBudgetCsv, importBudgetCsv } from "@/lib/workflow/budgetCsv";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: "Plan what each part of your event should cost, then record what you actually spend and see at once where you are over or under. Spending is entered as expenses, so the same entries feed the other event tools in your project. Works in any currency.",
  how: "Set the currency, then add a planned amount for each category (or split a total across a typical event). Add expenses as they happen with a category that matches a plan line. The table shows planned, spent and what is left, and flags anything over. Download Excel or CSV when you need to share it.",
  tip: "Record spending as deposits and payments happen, not at the end. Keep a contingency line: unplanned costs almost always appear. Category names are matched ignoring capital letters, so 'Venue' and 'venue' count as one.",
};

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "NZD", "INR", "JPY", "CNY", "SGD", "AED", "CHF", "SEK", "NOK", "DKK", "ZAR", "BRL", "MXN", "KRW", "PHP", "IDR", "THB", "TRY", "PLN"];
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const LEGACY = ["fdp-EventBudgetPlanner-totalBudget-v1", "fdp-EventBudgetPlanner-items-v1", "fdp-EventBudgetPlanner-nextId-v1"];

export default function EventBudgetPlanner() {
  const [project, store] = useProject();
  const { budgets, expenses } = project.data;
  const currency = project.currency;
  const { checkExport } = useExportGate();
  const [splitTotal, setSplitTotal] = useState("");
  const [splitType, setSplitType] = useState(Object.keys(BUDGET_SPLITS)[0]);
  const [newCategory, setNewCategory] = useState("");
  const [exp, setExp] = useState({ description: "", category: "", amount: "", taxIncluded: true });
  const [report, setReport] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const money = useMemo(() => {
    const make = (c: string) => { try { return new Intl.NumberFormat(project.locale || undefined, { style: "currency", currency: c, maximumFractionDigits: 2 }); } catch { return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }); } };
    const f = make(currency);
    return (n: number) => f.format(n);
  }, [currency, project.locale]);

  // Bring over a budget saved by the earlier version of this tool, once.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(LEGACY[1]);
      if (raw && store.get().data.budgets.length === 0) {
        const cur = store.get().currency;
        const m = migrateLegacyBudget(JSON.parse(raw), parseNumber, (p) => newId(p), today(), cur);
        if (m.lines.length) {
          store.set((p) => ({ ...p, data: { ...p.data, budgets: m.lines, expenses: [...p.data.expenses, ...m.expenses] } }));
          toast.success(`Brought over ${m.lines.length} budget lines from your earlier budget`);
        }
      }
      LEGACY.forEach((k) => sessionStorage.removeItem(k));
    } catch { /* storage blocked or unreadable: start empty */ }
  }, [store]);

  const rep = useMemo(() => budgetReport(budgets, expenses, currency), [budgets, expenses, currency]);
  const categories = useMemo(() => knownCategories(budgets, expenses), [budgets, expenses]);
  const costs = useMemo(() => [...expenses].filter((e) => e.kind === "cost").sort((a, b) => b.date.localeCompare(a.date)), [expenses]);
  const hasContingency = rep.rows.some((r) => /contingenc|buffer|reserve/i.test(r.category));

  const setPlanned = (id: string, value: string) => {
    const l = budgets.find((b) => b.id === id);
    const n = parseNumber(value);
    if (!l) return;
    store.upsertRecords("budgets", [{ ...l, planned: n !== null && n >= 0 ? n : 0 }]);
  };
  const removeLine = (id: string) => store.setRecords("budgets", budgets.filter((b) => b.id !== id));
  const addLine = (category: string, planned = 0) => {
    const c = category.trim().slice(0, 300);
    if (!c) return;
    if (budgets.some((b) => b.category.trim().toLowerCase() === c.toLowerCase())) { toast.message("That category already has a planned amount."); return; }
    store.upsertRecords("budgets", [{ id: newId("b"), category: c, planned, currency }]);
  };
  const applySplit = () => {
    const total = parseNumber(splitTotal);
    const lines = total ? splitBudget(total, splitType) : [];
    if (!lines.length) { toast.error("Enter your total budget first."); return; }
    const have = new Set(budgets.map((b) => b.category.trim().toLowerCase()));
    const fresh = lines.filter((l) => !have.has(l.category.toLowerCase()));
    if (!fresh.length) { toast.message("Those categories are already on your budget."); return; }
    store.setRecords("budgets", [...budgets, ...fresh.map((l) => ({ id: newId("b"), category: l.category, planned: l.planned, currency }))]);
    toast.success(`Added ${fresh.length} budget line${fresh.length > 1 ? "s" : ""}`);
  };
  const addExpense = () => {
    const amount = parseNumber(exp.amount);
    if (!exp.description.trim() || amount === null || amount <= 0) { toast.error("Add a description and an amount above zero."); return; }
    store.upsertRecords("expenses", [{
      id: newId("e"), date: today(), description: exp.description.trim().slice(0, 300), kind: "cost", category: exp.category.trim().slice(0, 300) || undefined,
      amount, currency, taxType: "none", taxIncluded: exp.taxIncluded,
    }]);
    setExp({ description: "", category: exp.category, amount: "", taxIncluded: exp.taxIncluded });
  };
  const removeExpense = (id: string) => store.setRecords("expenses", expenses.filter((e) => e.id !== id));

  const guard = async () => {
    if (!rep.rows.length) { toast.error("Add at least one budget line or expense first."); return false; }
    return checkExport();
  };
  const exportExcel = async () => {
    if (!(await guard())) return;
    const head = ["Category", "Planned", "Spent", "Remaining", "% used"];
    const rows: (string | number)[][] = [[project.name, `Budget in ${currency}`], [], head,
      ...rep.rows.map((r) => [r.category, r.planned, r.spent, r.remaining, r.percentUsed === null ? "" : r.percentUsed]),
      ["Total", rep.planned, rep.spent, rep.remaining, rep.planned > 0 ? Math.round((rep.spent / rep.planned) * 100) : ""]];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 30 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Budget");
    const eh = ["Date", "Description", "Category", "Vendor", "Amount", "Tax included", "Total paid"];
    const ew = XLSX.utils.aoa_to_sheet([eh, ...costs.map((e) => [e.date, e.description, e.category ?? "", e.vendor ?? "", e.amount, e.taxIncluded ? "yes" : "no", expenseGross(e)])]);
    ew["!cols"] = [{ wch: 12 }, { wch: 34 }, { wch: 20 }, { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ew, "Expenses");
    downloadBlob(new Blob([XLSX.write(wb, { type: "array", bookType: "xlsx" })], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "event-budget.xlsx");
    toast.success("Budget downloaded");
  };
  const exportCsv = async () => {
    if (!rep.rows.length) { toast.error("Add at least one budget line or expense first."); return; }
    if (!(await checkExport())) return;
    downloadBlob(new Blob([exportBudgetCsv(budgets, expenses, currency)], { type: "text/csv;charset=utf-8" }), "event-budget.csv");
    toast.success("Budget downloaded");
  };
  const importFile = async (f: File | undefined) => {
    if (!f) return;
    const r = importBudgetCsv(await f.text(), { currency });
    const errors = r.issues.filter((i) => i.level === "error");
    if (r.budgets.length || r.expenses.length) {
      const byId = new Map(budgets.map((b) => [b.id, b]));
      r.budgets.forEach((b) => byId.set(b.id, b));
      store.setRecords("budgets", Array.from(byId.values()));
      if (r.expenses.length) {
        const eById = new Map(expenses.map((x) => [x.id, x]));
        r.expenses.forEach((x) => eById.set(x.id, x));
        store.setRecords("expenses", Array.from(eById.values()));
      }
      toast.success(`${describeImport({ ...r, records: r.budgets, unknownColumns: [] }, "budget lines")}${r.expenses.length ? ` ${r.expenses.length} expense${r.expenses.length > 1 ? "s" : ""} restored.` : ""}`);
    } else toast.error(errors[0]?.message ?? "Nothing could be imported");
    setReport(r.issues.length ? r.issues.slice(0, 8).map((i) => `Row ${i.row}: ${i.message}`).join("\n") : null);
  };

  return (
    <ToolContainer>
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div><Label className="text-xs">Currency</Label>
          <select className="border rounded px-2 py-2 bg-background text-sm block" aria-label="Currency" value={currency} data-testid="bud-currency"
            onChange={(e) => {
              const next = e.target.value, prev = currency;
              // A currency choice, not a conversion: amounts entered in the old currency are relabelled.
              store.set((p) => ({ ...p, currency: next, data: { ...p.data,
                budgets: p.data.budgets.map((b) => (b.currency === prev ? { ...b, currency: next } : b)),
                expenses: p.data.expenses.map((x) => (x.currency === prev ? { ...x, currency: next } : x)) } }));
            }}>
            {(CURRENCIES.includes(currency) ? CURRENCIES : [currency, ...CURRENCIES]).map((c) => <option key={c} value={c}>{c}</option>)}
          </select></div>
        <p className="text-xs text-muted-foreground max-w-md pb-2">Changing the currency relabels your amounts; it does not convert them. Lines or expenses in a different currency are left out and flagged.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5" data-testid="bud-tiles">
        <Card className="p-4 text-center"><p className="text-xl font-bold">{money(rep.planned)}</p><p className="text-xs text-muted-foreground mt-1">Planned</p></Card>
        <Card className="p-4 text-center"><p className="text-xl font-bold">{money(rep.spent)}</p><p className="text-xs text-muted-foreground mt-1">Spent so far</p></Card>
        <Card className="p-4 text-center"><p className={`text-xl font-bold ${rep.remaining >= 0 ? "text-green-600" : "text-red-600"}`}>{money(rep.remaining)}</p><p className="text-xs text-muted-foreground mt-1">{rep.remaining >= 0 ? "Left to spend" : "Over budget"}</p></Card>
      </div>

      {rep.otherCurrency > 0 && <NoticeBox className="text-sm mb-3" role="alert">{rep.otherCurrency} line{rep.otherCurrency > 1 ? "s are" : " is"} in another currency and not counted here.</NoticeBox>}
      {rep.unbudgeted > 0 && <NoticeBox className="text-sm mb-3" testId="bud-unbudgeted">{money(rep.unbudgeted)} was spent in categories with no planned amount. Give them a planned amount below.</NoticeBox>}
      {rep.rows.length > 0 && !hasContingency && <p className="text-xs text-muted-foreground mb-3">Tip: add a Contingency line (many planners use about 5 to 10 percent) for costs you cannot foresee.</p>}

      <div className="space-y-2" data-testid="bud-rows">
        {rep.rows.length === 0 && <p className="text-sm text-muted-foreground">No budget yet. Split a total below or add a category.</p>}
        {rep.rows.map((r) => {
          const pct = r.percentUsed ?? (r.spent > 0 ? 100 : 0);
          return (
            <div key={r.category} className={`rounded-lg border p-3 ${r.over ? "border-red-300 bg-red-50 dark:bg-red-950/20" : ""}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex-1 min-w-[8rem] text-sm font-medium">{r.category}</span>
                {r.lineId ? (
                  <Input className="w-28" inputMode="decimal" aria-label={`Planned for ${r.category}`} defaultValue={String(r.planned)} key={`${r.lineId}-${r.planned}`}
                    onBlur={(e) => setPlanned(r.lineId!, e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} />
                ) : (
                  <Button size="sm" variant="outline" onClick={() => addLine(r.category, r.spent)}>Plan {money(r.spent)}</Button>
                )}
                <span className="w-28 text-right text-sm">{money(r.spent)}</span>
                <span className={`w-28 text-right text-sm font-medium ${r.remaining >= 0 ? "text-green-600" : "text-red-600"}`}>{money(r.remaining)}</span>
                {r.lineId && <Button size="icon" variant="ghost" aria-label={`Remove ${r.category}`} onClick={() => removeLine(r.lineId!)}><Trash2 className="h-4 w-4" /></Button>}
              </div>
              <div className="h-1.5 rounded bg-muted overflow-hidden mt-2"><div className={`h-full ${r.over ? "bg-red-500" : "bg-green-600"}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
            </div>
          );
        })}
        {rep.rows.length > 0 && <p className="text-xs text-muted-foreground">Columns: planned (editable), spent, left.</p>}
      </div>

      <div className="flex gap-2 mt-3">
        <Input value={newCategory} maxLength={300} onChange={(e) => setNewCategory(e.target.value)} placeholder="Add a category (e.g. Venue)" aria-label="New category" list="bud-cats"
          onKeyDown={(e) => { if (e.key === "Enter") { addLine(newCategory); setNewCategory(""); } }} data-testid="bud-newcat" />
        <Button variant="outline" onClick={() => { addLine(newCategory); setNewCategory(""); }} aria-label="Add category"><Plus className="h-4 w-4" /></Button>
      </div>
      <datalist id="bud-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>

      <div className="rounded-xl border p-4 mt-5 space-y-3">
        <p className="text-sm font-medium">Split a total budget</p>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">Total budget ({currency})</Label><Input className="w-36" inputMode="decimal" value={splitTotal} onChange={(e) => setSplitTotal(e.target.value)} aria-label="Total budget" data-testid="bud-total" /></div>
          <div><Label className="text-xs">Event type</Label>
            <select className="border rounded px-2 py-2 bg-background text-sm block" aria-label="Event type" value={splitType} onChange={(e) => setSplitType(e.target.value)}>
              {Object.keys(BUDGET_SPLITS).map((n) => <option key={n} value={n}>{n}</option>)}
            </select></div>
          <Button onClick={applySplit} data-testid="bud-split">Add typical categories</Button>
        </div>
        <p className="text-xs text-muted-foreground">A starting point only: shares are rule-of-thumb and vary by place and event. Change any amount afterwards.</p>
      </div>

      <div className="rounded-xl border p-4 mt-5 space-y-3">
        <p className="text-sm font-medium">Record an expense</p>
        <div className="flex flex-wrap items-end gap-2">
          <Input className="flex-1 min-w-[10rem]" placeholder="What was it?" maxLength={300} aria-label="Expense description" value={exp.description} onChange={(e) => setExp({ ...exp, description: e.target.value })} data-testid="bud-exp-desc" />
          <Input className="w-40" placeholder="Category" list="bud-cats" aria-label="Expense category" value={exp.category} onChange={(e) => setExp({ ...exp, category: e.target.value })} data-testid="bud-exp-cat" />
          <Input className="w-28" inputMode="decimal" placeholder="Amount" aria-label="Expense amount" value={exp.amount} onChange={(e) => setExp({ ...exp, amount: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addExpense()} data-testid="bud-exp-amt" />
          <Button onClick={addExpense} data-testid="bud-exp-add">Add</Button>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground"><input type="checkbox" checked={exp.taxIncluded} onChange={(e) => setExp({ ...exp, taxIncluded: e.target.checked })} /> Amount already includes any tax</label>
        {costs.length > 0 && (
          <div className="space-y-1" data-testid="bud-exp-list">
            {costs.slice(0, 20).map((e) => (
              <div key={e.id} className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground w-24 shrink-0">{e.date}</span>
                <span className="flex-1 truncate">{e.description}{e.category ? <span className="text-muted-foreground"> ({e.category})</span> : null}</span>
                <span>{e.currency === currency ? money(expenseGross(e)) : `${e.amount} ${e.currency}`}</span>
                <Button size="icon" variant="ghost" aria-label={`Remove ${e.description}`} onClick={() => removeExpense(e.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            {costs.length > 20 && <p className="text-xs text-muted-foreground">Showing the latest 20 of {costs.length}. All are counted and exported.</p>}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <Button variant="outline" onClick={exportExcel} disabled={!rep.rows.length} className="gap-2"><Download className="h-4 w-4" /> Download Excel</Button>
        <Button variant="outline" onClick={exportCsv} disabled={!rep.rows.length} className="gap-2"><Download className="h-4 w-4" /> Download budget and expenses CSV</Button>
        <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2"><Upload className="h-4 w-4" /> Import budget CSV</Button>
        <ExportUsageNote className="basis-full" />
        <Button variant="ghost" size="sm" onClick={() => downloadBlob(new Blob([templateCsv("budgets")], { type: "text/csv;charset=utf-8" }), "budget-template.csv", { free: true })}>Blank CSV</Button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { void importFile(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
      {report && <pre className="mt-3 text-xs whitespace-pre-wrap rounded border p-3 bg-muted/30">{report}</pre>}
      <ProjectBar />
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
